import React, { useState, useEffect } from "react";
import { useUserContext } from "contexts/UserContext";
import useDataSourceManager from "hooks/useDataSourceManager";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidWarnButton } from "components/buttons/SolidWarnButton";
import ModalContainer from "components/ModalContainer";

import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show.svg";

const DeleteUserModal = (props) => {
  const {
    openDeleteAccountModal,
    handleCloseDeleteAccountModal,
    showMember,
    setShowMember,
    showInvitation,
    setShowInvitation,
    selectedRowData,
    entId,
    orgId,
    getListOrgUsers,
    getListPendingOrgUsers,
    setDrawerOpen,
  } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [originUser, setOriginUser] = useState(null);
  const [checkResponse, setCheckResponse] = useState(null);
  const [errorPasswordMessage, setErrorPasswordMessage] = useState("");
  const [deleteUserUuid, setDeleteUserUuid] = useState(uuidv4());
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSuperAdminModal, setLastSuperAdminModal] = useState(false);
  const [removeUserModal, setRemoveUserModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  const { userState } = useUserContext();
  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const stored = localStorage.getItem("selectBreadcrumbs");
  const orgName = hasManageOrg
    ? JSON.parse(stored)?.orgs?.name ?? null
    : JSON.parse(stored)?.realm?.name ?? null;
  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager();

  const textfieldStyle = {
    width: 286,
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "30px",
      "& fieldset": {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.mainHover,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-selected fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  // casdoor get user
  useEffect(() => {
    const fetchUserSetting = async () => {
      if (userState && process.env.REACT_APP_IS_MOCKUP === "false") {
        try {
          if (userState) {
            setOriginUser(userState);
          }
        } catch (error) {
          console.error("Failed to fetch user setting:", error);
        }
      }
    };

    fetchUserSetting();
  }, [userState]);

  const deleteUsers = () => {
    const isUser = !!selectedRowData.lastLoginTime;

    const resourcePath = hasManageOrg
      ? isUser
        ? "/ents/:entId/orgs/:orgId/users/:userId.delete"
        : "/ents/:entId/orgs/:orgId/invitations/:invitationId.delete"
      : isUser
      ? "/orgs/:orgId/users/:userId.delete"
      : "/orgs/:orgId/invitations/:invitationId.delete";

    const restfulParams = hasManageOrg
      ? isUser
        ? { entId, orgId, userId: selectedRowData.id }
        : { entId, orgId, invitationId: selectedRowData.invitationId }
      : isUser
      ? { orgId, userId: selectedRowData.id }
      : { orgId, invitationId: selectedRowData.invitationId };

    const requestParams = isUser
      ? selectedRowData.id
      : selectedRowData.invitationId;

    sendRequest(resourcePath, deleteUserUuid, restfulParams, requestParams);
  };

  // casdoor check password
  const checkPassword = async () => {
    /* if (!originUser) return; */

    const userToCheck = {
      ...originUser,
      password,
    };

    try {
      const response = await sendRequest(
        "casdoor/UserService/.checkUserPassword",
        null,
        null,
        userToCheck
      );
      setCheckResponse(response);

      if (response.status === "ok") {
        setIsLoading(true);
        setErrorPasswordMessage("");
        deleteUsers();
      } else {
        if (response.msg?.includes("||||")) {
          const [msg, replace] = response.msg.split("||||");
          const replacedMsg = msg.replace(/%d/g, replace.trim());
          setErrorPasswordMessage(replacedMsg);
        } else {
          setErrorPasswordMessage(response.msg);
        }
      }
    } catch (err) {
      setErrorPasswordMessage("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    const deleteUsersResponse = getData(deleteUserUuid);
    if (!deleteUsersResponse) return;

    setIsLoading(false);

    if (deleteUsersResponse?.code === 400) {
      setLastSuperAdminModal(true);
      setErrorMessage(deleteUsersResponse?.message);
    } else {
      setRemoveUserModal(true);
      handleCloseDeleteAccountModal();
      setPassword("");
      getListOrgUsers();
      getListPendingOrgUsers();
      if (showInvitation) setShowInvitation(false);
      if (showMember) setShowMember(false);
    }
  }, [dataMap]);

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const resetStateAndClose = () => {
    setPassword("");
    setErrorPasswordMessage("");
    handleCloseDeleteAccountModal();
  };

  const removeUser = () => {
    setDrawerOpen(false);
    setRemoveUserModal(false);
  };

  return (
    <>
      <ModalContainer display={openDeleteAccountModal} modalWidth="500px">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
          </Box>
        )}
        <Box style={{ display: "flex", justifyContent: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Remove this user from this {orgName}
          </Typography>
        </Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "30px",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.black,
              fontSize: theme.typography.customFontSize[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Enter your password to remove account
          </Typography>
        </Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "10px",
          }}
        >
          <TextField
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={textfieldStyle}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowPassword}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <ShowIcon /> : <HideIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box>
          {errorPasswordMessage && (
            <Typography
              sx={{
                color: theme.palette.primary.redPrimary,
                fontSize: theme.typography.customFontSize[1],
                margin: "15px 0 0 0",
                textAlign: "center",
              }}
            >
              {errorPasswordMessage}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={resetStateAndClose}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidWarnButton
            onClick={checkPassword}
            width="100px"
            height="32px"
            disabled={!password}
          >
            Remove
          </SolidWarnButton>
        </Box>
      </ModalContainer>
      <ModalContainer display={removeUserModal} modalWidth="500px">
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            <div> {selectedRowData?.email} </div>has been removed
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                marginTop: "20px",
              }}
            >
              This user has been successfully removed.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <SolidButton
              sx={{ marginTop: "30px" }}
              onClick={removeUser}
              width="100px"
              height="32px"
            >
              OK
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>
      <ModalContainer display={lastSuperAdminModal} modalWidth="500px">
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Action denied
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                marginTop: "20px",
              }}
            >
              You are currently the last <strong>Super Admin</strong>. <br />
              Please assign another user before leaving or changing your role.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <SolidButton
              sx={{ marginTop: "30px" }}
              onClick={() => setLastSuperAdminModal(false)}
              width="100px"
              height="32px"
            >
              OK
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>
    </>
  );
};

export default DeleteUserModal;
